import React, { useEffect } from 'react';
import styled from 'styled-components';

const LOGO_URL = 'https://shovelapp.io/wp-content/uploads/2025/12/logo-Shovel.png';

const IOS_URL = 'https://apps.apple.com/cz/app/shovel-study-planner/id1467742357';

const ANDROID_URL = 'https://play.google.com/store/apps/details?id=com.howtostudyincollege.shovel';

type Props = {
  appName?: string;
  companyName?: string;
  tagline?: string;
  description?: string;
  iconUrl: string;
  appStoreUrl: string;
  playStoreUrl: string;
  autoRedirect?: boolean;
};

export default function MobileRedirectToAppOrPlayStore({
  appName = 'Shovel - Study Planner',
  companyName = 'How to Study Smart, LLC',
  tagline = 'Your Personal Study Planner',
  description = 'Plan your time, track tasks, and stay on top of your study schedule',
  iconUrl = LOGO_URL,
  appStoreUrl = IOS_URL,
  playStoreUrl = ANDROID_URL,
  autoRedirect = true
}: Props) {
  useEffect(() => {
    if (!autoRedirect) return;

    const ua = navigator.userAgent || '';
    const isAndroid = /android/i.test(ua);
    const isIOS = /iPad|iPhone|iPod/.test(ua);

    const t = window.setTimeout(() => {
      if (isIOS) window.location.replace(appStoreUrl);
      else if (isAndroid) window.location.replace(playStoreUrl);
    }, 200);

    return () => window.clearTimeout(t);
  }, [autoRedirect, appStoreUrl, playStoreUrl]);

  return (
    <Page>
      <Container>
        <Title>{appName}</Title>
        <Company>{companyName}</Company>

        <IconWrap>
          <IconCard aria-label={`${appName} icon`}>
            <IconImg src={iconUrl} alt={`${appName} icon`} />
          </IconCard>
        </IconWrap>

        <Tagline>{tagline}</Tagline>
        <Desc>{description}</Desc>

        <Hint>If you are not redirected automatically, use the buttons bellow</Hint>

        <Buttons>
          <StoreButton href={appStoreUrl} rel="noopener noreferrer">
            App Store
          </StoreButton>
          <StoreButton href={playStoreUrl} rel="noopener noreferrer">
            Play Store
          </StoreButton>
        </Buttons>
      </Container>
    </Page>
  );
}

const Page = styled.div`
  background: #5f63d8;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 16px;
  box-sizing: border-box;
  color: #ffffff;
  width: 100%;

  div {
    font-family: 'Quicksand', sans-serif;
  }
`;

const Container = styled.div`
  width: 100%;
  max-width: 760px;
  text-align: center;
`;

const Title = styled.div`
  margin: 0;
  font-weight: 800;
  letter-spacing: 0.2px;
  font-size: 26px;
  line-height: 1.1;
`;

const Company = styled.div`
  margin-top: 10px;
  font-size: 16px;
  opacity: 0.75;
  font-weight: 500;
`;

const IconWrap = styled.div`
  margin: 20px auto 0;
  display: flex;
  justify-content: center;
`;

const IconCard = styled.div`
  width: 100px;
  height: 100px;
  background: rgba(255, 255, 255, 0.96);
  border-radius: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.18);
`;

const IconImg = styled.img`
  width: 80px;
  height: 80px;
  object-fit: contain;
  display: block;
`;

const Tagline = styled.div`
  margin-top: 40px;
  font-weight: 800;
  font-size: 22px;
  line-height: 1.15;
`;

const Desc = styled.div`
  margin: 16px auto 0;
  max-width: 640px;
  font-size: 16px;
  line-height: 1.55;
  opacity: 0.75;
  font-weight: 500;
`;

const Hint = styled.div`
  margin: 24px auto 0;
  max-width: 660px;
  font-size: 14px;
  line-height: 1.5;
  opacity: 0.9;
  font-weight: 700;
`;

const Buttons = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
`;

const StoreButton = styled.a`
  padding: 12px 16px;
  border-radius: 10px;
  background: rgba(140, 146, 255, 0.55);
  color: #ffffff;
  text-decoration: none;
  font-weight: 800;
  font-size: 16px;
  box-shadow: 0 14px 28px rgba(0, 0, 0, 0.12);
  display: inline-block;
  user-select: none;

  &:hover {
    filter: brightness(1.05);
  }

  &:active {
    transform: translateY(1px);
  }
`;
